import React, {useState, useEffect, useCallback, useRef} from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import TopBar from './components/TopBar';
import Footer from './components/Footer';
import Home from './components/Home';
import Shop from './components/Shop';
import About from './components/About';
import Login from './components/Login';
import './i18n';
import {trackEvent} from "./utils/Analytics";
import {useTranslation} from "react-i18next";

function App() {
    const [currentView, setCurrentView] = useState('homepage');
    const [showLogin, setShowLogin] = useState(false);
    const [prefillId, setPrefillId] = useState(() => localStorage.getItem('prefillId') || '');
    const [iframeEl, setIframeEl] = useState(null);
    const [canvasInstance, setCanvasInstance] = useState(null);
    const [showLoginButton, setShowLoginButton] = useState(true);
    const [showStoreButton, setShowStoreButton] = useState(true);
    const [loginErrorMessage, setMessage] = useState("");
    const { t } = useTranslation();
    const isAutoLogin = !!prefillId;
    const isAutoLoggingIn = useRef(false); // Use a simple variable to track auto-login state
    const [userId, setUserId] = useState('');
    const [loading, setLoading] = useState(false);
    const [topBarVisible, setTopBarVisible] = useState(true);
    const iframeContainerRef = useRef(null);
    const [homeScroll, setHomeScroll] = useState(0);
    const [shopScroll, setShopScroll] = useState(0);
    const [docBodyMinHeight, setDocBodyMinHeight] = useState("");
    const [isFirstLoad, setIsFirstLoad] = useState(true);
    const [shouldBlockScroll, setShouldBlockScroll] = useState(false);
    const mainContentRef = useRef(null);

    useEffect(() => {
        const topBarElement = document.querySelector('.fixed-header');
        const mainContentElement = mainContentRef.current;

        if (topBarElement && mainContentElement) {
            const resizeObserver = new ResizeObserver(() => {
                const topBarHeight = topBarElement.offsetHeight;
                mainContentElement.style.paddingTop = `${topBarHeight}px`;
            });

            resizeObserver.observe(topBarElement);

            return () => resizeObserver.disconnect();
        }
    }, []);
    
    // Whenever prefillId changes, update localStorage
    useEffect(() => {
        localStorage.setItem('prefillId', prefillId);
    }, [prefillId]);
    
    useEffect(() => {
        // Save scroll position when leaving a view
        return () => {
            if (currentView === 'home' || currentView === 'homepage') setHomeScroll(window.scrollY);
            if (currentView === 'store' && !shouldBlockScroll) setShopScroll(window.scrollY);
        };
    }, [currentView,shouldBlockScroll]);
    
    useEffect(() => {
        if (currentView === 'store' && canvasInstance) {
            trackEvent('shop_view_shown_with_canvas', 'navigation', 'Shop View with Canvas');
        }
    }, [currentView, canvasInstance]);
    
    useEffect(() => {
        // Restore scroll position when entering a view
        if (currentView === 'home' || currentView === 'homepage' || currentView === 'store') {
            setTimeout(() => {
                const targetScroll = currentView === 'home' || currentView === 'homepage' ? homeScroll : shopScroll;
                const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
                window.scrollTo(0, Math.min(targetScroll, maxScroll));
            }, 50);
        }
    }, [currentView, homeScroll, shopScroll]);

    useEffect(() => {
        if (shouldBlockScroll && currentView === 'store') {
            document.body.style.overflow = 'hidden';
            document.body.style.position = 'fixed'; // Optional: prevents some Android scroll issues
            document.body.style.width = '100%';
        } else {
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
            setTimeout(() => {
                const targetScroll = currentView === 'home' || currentView === 'homepage' ? homeScroll : shopScroll;
                const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
                window.scrollTo(0, Math.min(targetScroll, maxScroll));
            }, 50);
        }
        return () => {
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
        };
    }, [shouldBlockScroll,currentView,homeScroll, shopScroll]);

    useEffect(() => {
        // Set minHeight for the document body based on the current view so that Home does not have empty space scolling at the bottom
        if (currentView === 'store') {
            // Set minHeight for shop view (e.g., based on iframe or content)
            if(iframeEl !== null && document.body.style.minHeight !== docBodyMinHeight)
            {
                document.body.style.minHeight = docBodyMinHeight; // or your dynamic value
            }
        } else {
            // Reset minHeight for home/other views
            if( document.body.style.minHeight !== '')
            {
                setDocBodyMinHeight(document.body.style.minHeight);
            }
            document.body.style.minHeight = '';
        }
    }, [currentView,docBodyMinHeight,iframeEl]);
    
    const navigate = useNavigate();

    // Update navigation:
    const handleNavigate = useCallback((view) => {
        if (view === 'home')
        {
            setShowStoreButton(false);
            setShowLoginButton(false);
        }
        else
        {
            setShowStoreButton(true);
        }
        setCurrentView(view);
        navigate(`/${view}`);
    }, [navigate]);
    
    const onClose = useCallback((connection) => {
        //console.log("onClose", connection);
        setShowLogin(false);
        if(!connection)
        {
           // console.log("onClose !connection");
            setShowLoginButton(true);
            if (currentView === 'store') {
            //    console.log("onClose !connection home");
                handleNavigate('homepage');
            }
        }
    }, [currentView,handleNavigate]);

    useEffect(() => {
        if (currentView !== 'store') {
            setShowLogin(false);
        }
    }, [currentView]);

    useEffect(() => {
        if (currentView === 'store' && iframeEl && iframeContainerRef.current) {
            if (iframeEl.parentNode !== iframeContainerRef.current) {
                iframeContainerRef.current.appendChild(iframeEl);
            }
        }
    }, [currentView, iframeEl]);
    
    useEffect(() => {
        if (!iframeEl) return;

        let lastHeight = null;
        const updateHeight = () => {
            const minHeight = window.getComputedStyle(document.body).minHeight;
            if (minHeight !== lastHeight) {
                lastHeight = minHeight;
                const minHeightValue = parseInt(minHeight, 10);
                const contentHeight = minHeightValue > 0 ? minHeightValue : 100;
                iframeContainerRef.current.style.height = `${contentHeight}px`;
            }
        };

        updateHeight();
        const interval = setInterval(updateHeight, 200);

        return () => clearInterval(interval);
    }, [iframeEl]);
    
    const onCanvasReady = useCallback((iframe, instance, showCanvas) => {
        setIframeEl(iframe);
        console.log("onCanvasReady ", instance, showCanvas);
        setCanvasInstance(instance);
        setShowLoginButton(false);
        if(showCanvas)
            handleNavigate('store');
    }, [handleNavigate]);

    
    const onCanvasExpired = useCallback((instance) => {
        setUserId('');
        console.log("onCanvasExpired",canvasInstance, instance);
        if(canvasInstance!==null && canvasInstance!==undefined)
        {
            //console.log("onCanvasExpired calling canvasInstance.destroy()",canvasInstance);
            canvasInstance.destroy();  
        }
        if(instance!==null && instance!==undefined)
        {
            //console.log("onCanvasExpired calling instance.destroy()",canvasInstance);
            instance.destroy();
        }
        setCanvasInstance(null);
        if (iframeContainerRef.current) {
            iframeContainerRef.current.innerHTML = '';
        }
        setIframeEl(null);
        setShowLoginButton(true);
        setShowLogin(true);
        handleNavigate('store');
    },[canvasInstance,handleNavigate]);
    
    const handleContinue = useCallback(async (userId, showCanvas=true) => {
        if(canvasInstance !== null && canvasInstance !== undefined) {
            setShowLogin(false);
            canvasInstance.show();
            onClose?.(true);
            //console.log("Canvas instance already exists, showing it directly.");
            return;
        }
        
        if (!userId.trim()) {
            setMessage(t('login.missingId'))
            return;
        }

        setMessage("");
        if(canvasInstance != null) {
            //console.log("Canvas instance already exists, showing it directly.");
            canvasInstance.show();
            return;
        }

        try {
            trackEvent('login_continue_click', 'login', 'Continue Button');
            setLoading(true);
            const serverUrl = process.env.REACT_APP_SERVER_URL;
            console.log(serverUrl);
            const response = await fetch(`https://${serverUrl}/auth/${userId}`);
            const token = await response.text();

            if (!window.CanvasAgent) {
                console.log(token);
                console.log(serverUrl);
                console.log(userId);
                alert(t('login.canvasError'));
                return;
            }

            const canvasAppId = process.env.REACT_APP_CANVAS_ID;
            console.log(canvasAppId);

            //console.log("setting instance ",userId);
            const instance = await window.CanvasAgent.create({
                canvasAppId,
                token,
                locale: 'en-US',
                fullScreen: false,  //takes awy scrolling
                eventListener: (event) => {

                    console.log(event);

                    if (event.eventType === 'ready') {
                        //console.log("Canvas ready event");
                        setTimeout(() => {
                            const iframe = document.querySelector('iframe[src*="canvas"]');
                          //  console.log("iframe",iframe,iframeContainerRef.current);
                            if (iframe && iframeContainerRef.current) {
                                if (iframe.parentNode !== iframeContainerRef.current) {
                                    iframe.parentNode?.removeChild(iframe);
                                    iframeContainerRef.current.appendChild(iframe);
                                }
                            }
                            setPrefillId(userId);
                            onCanvasReady(iframe, instance, showCanvas);
                        }, 100);
                        if(showCanvas)
                            instance.show();

                        setTimeout(() => {
                            setLoading(false);
                            onClose?.(true);
                        }, 3000);
                    }
                    
                    if(event.eventType === 'navigate') {
                        if (event.toRoute.name.includes('offer')) {
                            setShouldBlockScroll(true);
                            trackEvent('canvas_click_offer', 'canvasClickOffer', `Canvas Click Offer: ${event.toRoute.fullPath}`);
                        }
                        else
                        {
                            setShouldBlockScroll(false);
                        }
                    }

                    if(event.eventType === 'checkout') {
                            trackEvent('canvas_checkout_offer', 'canvasCheckoutOffer', `Canvas Checkout Offer: ${event.offerId}`);
                    }

                    // Handle error and expired events
                    if (event.eventType === 'error') {
                        setMessage(`${event.message} ID: ${userId}`)
                        setTimeout(() => {
                            setLoading(false);
                            onClose?.(false);
                            onCanvasExpired?.(instance);
                        }, 3000);
                        trackEvent('canvas_error', 'canvasError', `Canvas Error: ${event.message} userId: ${userId}`);
                    }
                    else if (event.eventType === 'expired') {
                        setMessage(t('login.sessionExpired'))
                        setTimeout(() => {
                            setLoading(false);
                            onCanvasExpired?.(instance);
                        }, 3000);
                        trackEvent('canvas_expired', 'canvasExpired', 'Canvas Expired');
                    }
                }
            });
            if(instance !== null && instance !== undefined)
            {
                //console.log("setting canvasInstance after await",instance);
                setCanvasInstance(instance);
            }
            console.log("Canvas instance created:", instance);
        } catch (err) {
            console.error("Canvas error:", err);
            trackEvent('canvas_exception', 'canvasException', `Canvas Exception: ${err.message}`);
            setMessage(t('login.shopError'))
            setLoading(false);
            onClose?.(false);
        }
    },  [onClose, onCanvasReady, t, canvasInstance, onCanvasExpired])
    
    useEffect(() => {
        if (isAutoLogin && userId.trim() && isAutoLoggingIn.current === false) {
            isAutoLoggingIn.current = true; // Set the flag to true to prevent re-entrance
            setShowLoginButton(false);
            setIsFirstLoad(false);
            handleNavigate('homepage');
            console.log("AutoLogin");
            handleContinue(userId,false);
            
        }
    }, [isAutoLogin, userId, handleContinue, isAutoLoggingIn, setShowLoginButton,handleNavigate]);
    
    useEffect(() => {
        const path = window.location.pathname.replace(/^\/+/, ''); // remove leading slashes

        if (path.startsWith('homepage/') && path !== currentView) {
            const id = path.substring('homepage/'.length);
            if (id) {
                console.log("setting homepage prefillId ", id);
                setPrefillId(id);
                setUserId(id);
                setCurrentView('homepage');
            }
        } if (path === '') {
            handleNavigate('home');
        }
        else if (path === 'home' || path === 'homepage' || path === 'store') {
           if( path !== currentView)
                handleNavigate(path);
        }
        else
        {
            handleNavigate('home');
        }
    }, [prefillId,handleNavigate,isFirstLoad,currentView,userId]);
    
    
    const handleTopBarVisibility = useCallback((visible) => {
        setTopBarVisible(visible);
    }, [setTopBarVisible]);
    
    const handleResize = useCallback (() => {
        if (!iframeEl) return;
        if (topBarVisible) {
            const topBarElement = document.querySelector('.fixed-header');
            if (topBarElement) {
                const topBarHeight = topBarElement.offsetHeight;
                Object.assign(iframeEl.style, {
                    top: `${topBarHeight}px`,
                });
            }
        }
        else
        {
            Object.assign(iframeEl.style, {
                top: '0',
            });
        }
    }, [iframeEl,topBarVisible])
    
    useEffect(() => {
        if (!iframeEl) return;
        
        handleResize();
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [iframeEl, handleResize]);

    
    const shouldLoginOrShowCanvas = () => {
        if(canvasInstance != null) {
            //console.log("shouldLoginOrShowCanvas: Canvas instance already exists, showing it directly.");
            canvasInstance.show();
            handleResize();
            return false;
        }
        //console.log("shouldLoginOrShowCanvas: Returning true to show login or canvas. canvasInstance",canvasInstance);
        return true;
    }
    
    return (
        <div className="app-container">
            <TopBar
                currentView={currentView}
                onNavigate={handleNavigate}
                onLoginClick={() => {handleNavigate('store');}}
                showLoginButton= {showLoginButton}
                showStoreButton = {showStoreButton}
                onVisibilityChange={handleTopBarVisibility}
                iframeEl={iframeEl}
                shouldBlockScroll={shouldBlockScroll}
            />
            <div
                className="iframe-container"
                ref={iframeContainerRef}
                style={{ display: currentView === 'store' ? 'block' : 'none' }}
            ></div>
            <main className="main-content" ref={mainContentRef}>
                <Routes>
                    <Route path="/home" element={<Home />} />
                    <Route path="/homepage" element={<Home />} />
                    <Route path="/store" element={<Shop shouldLoginOrShowCanvas={shouldLoginOrShowCanvas} setShowLogin={setShowLogin} isLoggedIn={showLoginButton===false}/>} />
                    <Route path="/about" element={<About />} />
                    <Route path="*" element={<Home />} />
                </Routes>
            </main>
            {(currentView !== 'store' || (canvasInstance == null)) && (<Footer />)}
            {
                showLogin && (
                <Login
                    onClose={onClose}
                    prefillId={prefillId}
                    handleContinue={handleContinue}
                    loginErrorMessage={loginErrorMessage}
                    loading={loading}
                />
            )}
        </div>
    );
}

export default App;
